/**
 * AdminSupportCampaignsPage — V9.19
 * Interface Admin AITO pour gérer les campagnes SupportCampaign V1
 * (Athlètes + Clubs · T-shirt · seuil 20 · précommandes uniquement).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { showError } from "@/lib/errors";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Shirt,
  Plus,
  RefreshCcw,
  ShieldCheck,
  ExternalLink,
  Trash2,
  Palette,
} from "lucide-react";

const DEFAULT_TEMPLATES = {
  black: "/tshirt-templates/default_black_preview.png",
  white: "/tshirt-templates/default_white_preview.png",
};

export default function AdminSupportCampaignsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null); // campagne courante
  const [editOpen, setEditOpen] = useState(false);
  const [preorders, setPreorders] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/support-campaigns");
      setItems(data.items || []);
    } catch (e) {
      showError(e, { prefix: "Chargement :" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = async (c) => {
    setEditing({ ...c });
    setEditOpen(true);
    try {
      const { data } = await api.get(
        `/admin/support-campaigns/${c.id}/preorders`,
      );
      setPreorders((p) => ({ ...p, [c.id]: data.items || [] }));
    } catch {}
  };

  const savePatch = async () => {
    if (!editing) return;
    try {
      await api.patch(`/admin/support-campaigns/${editing.id}`, {
        title: editing.title,
        description: editing.description,
        objective: editing.objective,
        tshirt_variants: editing.tshirt_variants,
        tshirt_price_xpf: editing.tshirt_price_xpf,
        custom_visual_url: editing.custom_visual_url || null,
        use_default_template: editing.use_default_template,
      });
      toast.success("Campagne mise à jour");
      setEditOpen(false);
      load();
    } catch (e) {
      showError(e, { prefix: "Sauvegarde :" });
    }
  };

  const approveVisual = async () => {
    if (!editing) return;
    try {
      await api.post(
        `/admin/support-campaigns/${editing.id}/approve-visual`,
      );
      toast.success("Visuel validé");
      setEditOpen(false);
      load();
    } catch (e) {
      showError(e, { prefix: "Validation :" });
    }
  };

  const resetDefault = async (id) => {
    try {
      await api.post(`/admin/support-campaigns/${id}/reset-to-default`);
      toast.success("Retour au template officiel");
      setEditOpen(false);
      load();
    } catch (e) {
      showError(e, { prefix: "Reset :" });
    }
  };

  const deactivate = async (id) => {
    if (!confirm("Archiver cette campagne ?")) return;
    try {
      await api.post(`/admin/support-campaigns/${id}/deactivate`);
      toast.success("Campagne archivée");
      load();
    } catch (e) {
      showError(e, { prefix: "Archivage :" });
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="admin-support-campaigns">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Shirt className="h-6 w-6" /> SupportCampaign V1
          </h1>
          <p className="text-sm text-muted-foreground">
            Campagnes de soutien T-shirt · Athlètes + Clubs · seuil 20 · 4 500
            XPF par défaut.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            data-testid="admin-support-refresh"
          >
            <RefreshCcw className="h-4 w-4 mr-1" /> Actualiser
          </Button>
          <Button
            onClick={() => setCreateOpen(true)}
            data-testid="admin-support-create-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Nouvelle campagne
          </Button>
        </div>
      </div>

      {/* Templates par défaut (aperçu) */}
      <Card className="p-4">
        <div className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Palette className="h-4 w-4" /> Templates officiels par défaut
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {(["black", "white"]).map((v) => (
            <div key={v} className="rounded-md overflow-hidden border bg-muted/30">
              <img
                src={DEFAULT_TEMPLATES[v]}
                alt={v}
                className="w-full h-56 object-contain"
                data-testid={`admin-default-template-${v}`}
              />
              <div className="p-2 text-xs text-center bg-muted/40 border-t">
                Version {v === "black" ? "noire" : "blanche"} · recto + verso
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Liste des campagnes */}
      {loading ? (
        <Skeleton className="h-40 w-full" />
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          Aucune campagne SupportCampaign V1 pour l'instant.
        </Card>
      ) : (
        <div className="grid gap-3">
          {items.map((c) => {
            const ben = c.beneficiary || {};
            const benName = ben.full_name || ben.name || "—";
            const total = Number(c.total_preorders_all_tranches || 0);
            const isActive = ["preorder_open", "threshold_reached", "confirmed_offline"].includes(c.status);
            return (
              <Card key={c.id} className="p-4" data-testid={`admin-support-campaign-${c.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-sm truncate">{c.title}</div>
                      <Badge variant="outline" className="text-[10px]">
                        {c.beneficiary_type === "club" ? "Club" : "Athlète"}
                      </Badge>
                      <Badge
                        variant={isActive ? "default" : "secondary"}
                        className="text-[10px]"
                      >
                        {c.status}
                      </Badge>
                      {c.custom_visual_url && !c.custom_visual_approved && (
                        <Badge variant="destructive" className="text-[10px]">
                          Visuel à valider
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Bénéficiaire : {benName} · {total} précommande{total > 1 ? "s" : ""} · {(c.tshirt_price_xpf || 4500).toLocaleString("fr-FR")} XPF
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/soutien/${c.slug}`} target="_blank">
                      <Button variant="ghost" size="sm">
                        <ExternalLink className="h-4 w-4 mr-1" /> Voir
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(c)}
                      data-testid={`admin-support-edit-${c.id}`}
                    >
                      Gérer
                    </Button>
                    {isActive && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deactivate(c.id)}
                        data-testid={`admin-support-deactivate-${c.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog Création */}
      <CreateCampaignDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={load}
      />

      {/* Dialog Edition */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Gérer la campagne</DialogTitle>
            <DialogDescription>
              Ajuster les paramètres, le prix et la présentation.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div>
                <Label>Titre</Label>
                <Input
                  value={editing.title || ""}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  rows={3}
                  value={editing.description || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, description: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>Objectif sportif</Label>
                <Input
                  value={editing.objective || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, objective: e.target.value })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Prix (XPF)</Label>
                  <Input
                    type="number"
                    min={100}
                    value={editing.tshirt_price_xpf || 4500}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        tshirt_price_xpf: parseInt(e.target.value, 10) || 0,
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Versions proposées</Label>
                  <div className="mt-2 flex items-center gap-3">
                    {["black", "white"].map((v) => (
                      <label key={v} className="flex items-center gap-1 text-xs">
                        <Checkbox
                          checked={(editing.tshirt_variants || []).includes(v)}
                          onCheckedChange={(chk) => {
                            const cur = new Set(editing.tshirt_variants || []);
                            if (chk) cur.add(v);
                            else cur.delete(v);
                            const arr = [...cur];
                            setEditing({
                              ...editing,
                              tshirt_variants: arr.length ? arr : ["black", "white"],
                            });
                          }}
                        />
                        {v === "black" ? "Noir" : "Blanc"}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="border-t pt-3">
                <Label className="flex items-center gap-2">
                  <Checkbox
                    checked={!!editing.use_default_template}
                    onCheckedChange={(chk) =>
                      setEditing({
                        ...editing,
                        use_default_template: !!chk,
                        custom_visual_url: chk ? null : editing.custom_visual_url,
                      })
                    }
                  />
                  Utiliser le template officiel par défaut
                </Label>
                {!editing.use_default_template && (
                  <div className="mt-2 space-y-2">
                    <div>
                      <Label>URL visuel personnalisé</Label>
                      <Input
                        placeholder="https://…"
                        value={editing.custom_visual_url || ""}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            custom_visual_url: e.target.value,
                          })
                        }
                      />
                    </div>
                    {editing.custom_visual_url && (
                      <div className="flex items-center gap-2">
                        {editing.custom_visual_approved ? (
                          <Badge className="bg-emerald-600 text-white">
                            Validé
                          </Badge>
                        ) : (
                          <Badge variant="destructive">Non validé</Badge>
                        )}
                        {!editing.custom_visual_approved && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={approveVisual}
                            data-testid="admin-support-approve-visual"
                          >
                            <ShieldCheck className="h-4 w-4 mr-1" /> Valider ce visuel
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => resetDefault(editing.id)}
                        >
                          Revenir au template
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Preorders récap */}
              {(preorders[editing.id] || []).length > 0 && (
                <div className="border-t pt-3">
                  <div className="text-sm font-semibold mb-2">
                    Précommandes ({(preorders[editing.id] || []).filter(p => p.status !== "cancelled").length} actives, {(preorders[editing.id] || []).filter(p => p.status === "cancelled").length} annulées)
                  </div>
                  <div className="max-h-60 overflow-y-auto space-y-1">
                    {preorders[editing.id].map((p) => {
                      const cancelled = p.status === "cancelled";
                      return (
                        <div
                          key={p.id}
                          className={`text-xs flex items-center gap-2 border rounded p-1.5 ${cancelled ? "opacity-50 line-through" : ""}`}
                          data-testid={`admin-support-preorder-${p.id}`}
                        >
                          <span className="flex-1">
                            {p.first_name} {p.last_name} ({p.email})
                          </span>
                          <span className="text-muted-foreground">{p.variant || "—"}</span>
                          {cancelled ? (
                            <Badge variant="outline" className="text-[9px]">annulée</Badge>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 text-[10px] px-2"
                              onClick={async () => {
                                if (!confirm("Annuler cette précommande ?")) return;
                                try {
                                  await api.post(`/admin/support-campaigns/${editing.id}/preorders/${p.id}/cancel`, {});
                                  toast.success("Précommande annulée");
                                  // Refresh
                                  const { data } = await api.get(`/admin/support-campaigns/${editing.id}/preorders`);
                                  setPreorders((prev) => ({ ...prev, [editing.id]: data.items || [] }));
                                  load();
                                } catch (e) {
                                  showError(e, { prefix: "Annulation :" });
                                }
                              }}
                              data-testid={`admin-support-cancel-preorder-${p.id}`}
                            >
                              Annuler
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Fermer
            </Button>
            <Button onClick={savePatch} data-testid="admin-support-save">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Dialog création --------
function CreateCampaignDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    beneficiary_type: "athlete",
    beneficiary_id: "",
    title: "",
    description: "",
    objective: "",
    tshirt_variants: ["black", "white"],
    tshirt_price_xpf: 4500,
    use_default_template: true,
    custom_visual_url: "",
    activate_immediately: true,
  });
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [candidates, setCandidates] = useState([]);

  useEffect(() => {
    if (!search || search.length < 2) {
      setCandidates([]);
      return;
    }
    let cancelled = false;
    const q = encodeURIComponent(search);
    const path = form.beneficiary_type === "club" ? "clubs" : "athletes";
    api
      .get(`/${path}?search=${q}&limit=8`)
      .then((r) => {
        if (!cancelled) setCandidates(r.data.items || r.data || []);
      })
      .catch(() => {
        if (!cancelled) setCandidates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [search, form.beneficiary_type]);

  const submit = async () => {
    if (!form.beneficiary_id || !form.title) {
      toast.error("Bénéficiaire et titre requis.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/support-campaigns", {
        ...form,
        custom_visual_url: form.custom_visual_url || null,
      });
      toast.success("Campagne créée");
      onOpenChange(false);
      onCreated?.();
      setForm({
        beneficiary_type: "athlete",
        beneficiary_id: "",
        title: "",
        description: "",
        objective: "",
        tshirt_variants: ["black", "white"],
        tshirt_price_xpf: 4500,
        use_default_template: true,
        custom_visual_url: "",
        activate_immediately: true,
      });
    } catch (e) {
      showError(e, { prefix: "Création :" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Nouvelle campagne SupportCampaign V1</DialogTitle>
          <DialogDescription>
            T-shirt AITO.PF · Précommandes uniquement · seuil 20.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <Label>Bénéficiaire</Label>
            <div className="mt-1 flex items-center gap-2">
              <Select
                value={form.beneficiary_type}
                onValueChange={(v) =>
                  setForm({ ...form, beneficiary_type: v, beneficiary_id: "" })
                }
              >
                <SelectTrigger
                  className="w-32"
                  data-testid="admin-support-create-btype"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="athlete">Athlète</SelectItem>
                  <SelectItem value="club">Club</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Rechercher…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="admin-support-create-search"
              />
            </div>
            {candidates.length > 0 && (
              <div className="mt-1 border rounded-md max-h-40 overflow-y-auto">
                {candidates.map((cand) => {
                  const label =
                    cand.full_name || cand.name || cand.id;
                  return (
                    <button
                      key={cand.id}
                      type="button"
                      onClick={() => {
                        setForm({ ...form, beneficiary_id: cand.id, title: form.title || `AITO x ${label} — T-shirt` });
                        setSearch(label);
                        setCandidates([]);
                      }}
                      className={`w-full text-left px-2 py-1.5 text-sm hover:bg-muted ${
                        form.beneficiary_id === cand.id ? "bg-muted" : ""
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
            {form.beneficiary_id && (
              <div className="text-[11px] text-muted-foreground mt-1">
                ID sélectionné : {form.beneficiary_id}
              </div>
            )}
          </div>

          <div>
            <Label>Titre</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="admin-support-create-title"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </div>
          <div>
            <Label>Objectif sportif (optionnel)</Label>
            <Input
              value={form.objective}
              onChange={(e) => setForm({ ...form, objective: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Prix (XPF)</Label>
              <Input
                type="number"
                min={100}
                value={form.tshirt_price_xpf}
                onChange={(e) =>
                  setForm({
                    ...form,
                    tshirt_price_xpf: parseInt(e.target.value, 10) || 0,
                  })
                }
                data-testid="admin-support-create-price"
              />
            </div>
            <div>
              <Label>Versions</Label>
              <div className="mt-2 flex items-center gap-3">
                {["black", "white"].map((v) => (
                  <label key={v} className="flex items-center gap-1 text-xs">
                    <Checkbox
                      checked={form.tshirt_variants.includes(v)}
                      onCheckedChange={(chk) => {
                        const s = new Set(form.tshirt_variants);
                        if (chk) s.add(v);
                        else s.delete(v);
                        const arr = [...s];
                        setForm({
                          ...form,
                          tshirt_variants: arr.length ? arr : ["black", "white"],
                        });
                      }}
                    />
                    {v === "black" ? "Noir" : "Blanc"}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <Checkbox
              checked={form.activate_immediately}
              onCheckedChange={(chk) =>
                setForm({ ...form, activate_immediately: !!chk })
              }
            />
            Activer immédiatement (ouvrir les précommandes)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !form.beneficiary_id || !form.title}
            data-testid="admin-support-create-submit"
          >
            {busy ? "Création…" : "Créer la campagne"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
